import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTwilioVoice } from '../context/TwilioVoiceContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Phone, PhoneOff, Pause, Play, ArrowRightLeft, UserPlus, 
  Users, Loader2, Clock, Mic, MicOff, X, Circle, StopCircle,
  Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'sonner';

// iter145 — Best-effort browser speakerphone toggle. setSinkId() is the
// official way to redirect HTMLMediaElement output to a non-default
// audio device; iOS Safari (incl. PWAs) does NOT implement it, and on
// iOS WebRTC always routes through the earpiece by design. We still
// expose the button so Android Chrome / desktop browsers can swap;
// iOS users get a clear toast explaining the limitation. A future
// Capacitor wrapper can take over via the native AudioSession API.
const trySetSpeakerOn = async (audioEl, on) => {
  if (!audioEl) return { ok: false, reason: 'no-audio-element' };
  if (typeof audioEl.setSinkId !== 'function') return { ok: false, reason: 'no-setsinkid' };
  try {
    // 'default' = system default (usually speaker in landscape, earpiece on
    // mobile portrait). For a true loud-speaker bias on Android we ask for
    // the 'communications' sink when available.
    const want = on ? 'default' : '';
    await audioEl.setSinkId(want);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || String(e) };
  }
};

const ActiveExternalCall = ({ callData, onCallEnd }) => {
  const { api, user } = useAuth();
  // For inbound PSTN calls, the live SDK call object is held in
  // TwilioVoiceContext (set by acceptIncoming) — `callData` itself only
  // carries the DB row. Fall back to that context call so things like
  // mute / disconnect work for inbound calls too.
  const twilioVoice = useTwilioVoice();
  const [callDuration, setCallDuration] = useState(0);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferType, setTransferType] = useState('user'); // 'user', 'supervisor', 'external'
  const [transferNumber, setTransferNumber] = useState('');
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  // 3-way state machine — null | 'dialing' | 'merged'
  const [threeWayState, setThreeWayState] = useState(null);
  const [threeWayKind, setThreeWayKind] = useState(null); // 'add' | 'transfer'
  // Live-recording toggle — defaults ON (recording is attached to the
  // conference by /voice/outgoing). Flipping OFF hits
  // /api/twilio/calls/{sid}/stop-recording which halts the Twilio
  // conference recording, announces the "recording stopped" clip to
  // all parties, and marks the call so the AI pipeline appends a
  // "System:" marker at the end of the transcript.
  const [recordingActive, setRecordingActive] = useState(!(callData?.recording_suppressed));
  const [stoppingRecording, setStoppingRecording] = useState(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  // Twilio CallSid normalisation. Outbound (placeCall) flows set `call_sid`
  // explicitly when the SDK assigns it. Inbound flows where the call data
  // arrives via /twilio/active-calls polling expose `twilio_call_sid`
  // (the raw DB field). Always honour both — using `call_sid` alone
  // caused the legacy poll path to declare every inbound call "ended"
  // ~3s after answer and silently terminate the bridge via clearActiveCall.
  const callSid = callData?.call_sid || callData?.twilio_call_sid;

  // Resolve the live SDK call object — outbound carries it as
  // `_sdkCall` on the active-call payload; inbound only has it inside
  // TwilioVoiceContext.activeCall.call. Without this fallback the
  // mute / hang-up handlers were no-ops for inbound calls.
  const liveSdkCall = callData?._sdkCall || twilioVoice?.activeCall?.call || null;

  useEffect(() => {
    // Start call timer
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    const sdkCall = liveSdkCall;
    if (sdkCall) {
      // SDK-driven call: trust the Twilio Voice SDK call object's lifecycle
      // events instead of polling the backend. (The DB row's twilio_call_sid
      // doesn't match the placeholder `sdk:...` key we passed in, so polling
      // would always think the call had ended.)
      const onDisconnect = () => onCallEnd?.();
      const onCancel = () => onCallEnd?.();
      const onError = (e) => {
        console.error('[ActiveExternalCall] SDK call error:', e);
        onCallEnd?.();
      };
      try { sdkCall.on('disconnect', onDisconnect); } catch (e) { /* ignore */ }
      try { sdkCall.on('cancel', onCancel); } catch (e) { /* ignore */ }
      try { sdkCall.on('error', onError); } catch (e) { /* ignore */ }

      // Still need eligible-users (Transfer modal needs them for SDK calls too).
      fetchEligibleUsers();

      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
        try { sdkCall.off?.('disconnect', onDisconnect); } catch (e) { /* ignore */ }
        try { sdkCall.off?.('cancel', onCancel); } catch (e) { /* ignore */ }
        try { sdkCall.off?.('error', onError); } catch (e) { /* ignore */ }
      };
    }

    // Legacy poll path — only used for calls placed via the old REST flow.
    pollRef.current = setInterval(pollCallStatus, 3000);
    fetchEligibleUsers();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pollCallStatus = async () => {
    try {
      const res = await api.get('/twilio/active-calls');
      const activeCalls = res.data.calls || [];
      const currentCall = activeCalls.find(c => c.twilio_call_sid === callSid);
      if (!currentCall) {
        // Call ended
        if (timerRef.current) clearInterval(timerRef.current);
        if (pollRef.current) clearInterval(pollRef.current);
        onCallEnd?.();
      }
    } catch (error) {
      // Ignore polling errors
    }
  };

  const fetchEligibleUsers = async () => {
    try {
      const res = await api.get('/twilio/contacts/eligible-users');
      setEligibleUsers((res.data.users || []).filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Failed to fetch eligible users:', error);
    }
  };

  const handleHold = async () => {
    setActionLoading('hold');
    const sid = callSid;
    if (!sid || String(sid).startsWith('sdk:')) {
      toast.error('Hold not available yet — wait for the call to fully connect.');
      setActionLoading(null);
      return;
    }
    try {
      if (isOnHold) {
        await api.post(`/twilio/calls/${sid}/resume`);
        setIsOnHold(false);
        toast.success('Call resumed');
      } else {
        await api.post(`/twilio/calls/${sid}/hold`);
        setIsOnHold(true);
        toast.success('Call on hold');
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update call');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopRecording = async () => {
    const sid = callSid;
    if (!sid || String(sid).startsWith('sdk:')) {
      toast.error('Recording controls not ready yet — wait for the call to connect.');
      return;
    }
    if (!recordingActive) {
      // Already stopped — no-op (user can't re-enable mid-call; that's a
      // compliance boundary).
      toast.message('Recording is already stopped for this call.');
      return;
    }
    setStoppingRecording(true);
    try {
      await api.post(`/twilio/calls/${sid}/stop-recording`);
      setRecordingActive(false);
      toast.success('Recording stopped — both parties notified.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not stop recording');
    } finally {
      setStoppingRecording(false);
    }
  };


  const handleEndCall = async () => {
    setActionLoading('end');
    // Local-first dismissal so the user is never stuck on the call screen.
    if (timerRef.current) clearInterval(timerRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
    // If this was placed via the Twilio Voice SDK, disconnect the actual
    // WebRTC call object — that's what really matters for hanging up.
    try {
      const sdkCall = liveSdkCall;
      if (sdkCall && typeof sdkCall.disconnect === 'function') {
        sdkCall.disconnect();
      }
    } catch (e) { /* ignore */ }
    onCallEnd?.();
    // Best-effort: tell the backend so the DB record + Twilio are also closed.
    try {
      if (callSid && !String(callSid).startsWith('sdk:')) {
        await api.post(`/twilio/calls/${callSid}/end`);
      }
      toast.success('Call ended');
    } catch (error) {
      // Idempotent backend now returns success even if the SID is stale, so
      // any remaining error here is purely informational.
      const detail = error?.response?.data?.detail || '';
      if (!/not found|already|completed|not active/i.test(detail)) {
        toast.message('Call ended (backend cleanup pending)');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleTransfer = async (targetUserId, toExternal, toSupervisor) => {
    // The Transfer button now uses the same dial-third primitive as Add
    // — the user tap "Transfer" → B is held + C is dialed; A continues
    // talking with C until they tap "Complete Transfer" (drops A,
    // leaves B↔C) or "Cancel Transfer" (drops C, unholds B). This
    // replaces the old "disconnect-A-immediately" flow that was
    // dropping calls on busy/no-answer.
    if (toSupervisor) {
      // Supervisor transfer still needs the resolution path on the
      // backend — re-use /transfer-call which now internally calls
      // dial-third after resolving the supervisor.
      setActionLoading('transfer');
      const sid = callSid;
      if (!sid || String(sid).startsWith('sdk:')) {
        toast.error('Transfer not available yet — wait for the call to fully connect.');
        setActionLoading(null);
        return;
      }
      try {
        await api.post('/twilio/transfer-call', {
          call_sid: sid,
          transfer_to_user_id: null,
          transfer_to_external: null,
          transfer_to_supervisor: true,
        });
        toast.success('Calling supervisor… Tap "Complete Transfer" once they answer.');
        setShowTransferModal(false);
        setThreeWayState('dialing');
        setThreeWayKind('transfer');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Transfer failed');
      } finally {
        setActionLoading(null);
      }
      return;
    }
    return handleAddParticipant(targetUserId, toExternal, 'transfer');
  };

  const handleAddParticipant = async (targetUserId, toExternal, kind = 'add') => {
    setActionLoading('add');
    const sid = callSid;
    if (!sid || String(sid).startsWith('sdk:')) {
      toast.error('Wait for the call to fully connect before adding a third party.');
      setActionLoading(null);
      return;
    }
    try {
      await api.post(`/twilio/calls/${sid}/dial-third`, {
        call_sid: sid,
        target_user_id: targetUserId || null,
        target_external: toExternal || null,
      });
      toast.success(
        kind === 'transfer'
          ? 'Calling… Tap "Complete Transfer" once they answer or "Cancel" to abort.'
          : 'Calling… Tap "Merge" once they answer or "End Merger" to drop them.'
      );
      setShowAddModal(false);
      setShowTransferModal(false);
      setThreeWayState('dialing');
      setThreeWayKind(kind);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dial third party');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMerge = async () => {
    setActionLoading('merge');
    try {
      await api.post(`/twilio/calls/${callSid}/merge`);
      toast.success('All three lines merged');
      setThreeWayState('merged');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEndMerger = async () => {
    setActionLoading('end_merger');
    try {
      await api.post(`/twilio/calls/${callSid}/end-merger`);
      toast.success('Third party dropped — back on the line with the original caller');
      setThreeWayState(null);
      setThreeWayKind(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'End merger failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCompleteTransfer = async () => {
    setActionLoading('complete_transfer');
    try {
      await api.post(`/twilio/calls/${callSid}/complete-transfer`);
      toast.success('Transfer complete');
      onCallEnd?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Unknown';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const getFullName = (u) => {
    if (u?.first_name && u?.last_name) return `${u.first_name} ${u.last_name}`;
    return u?.full_name || 'Unknown';
  };

  return (
    <>
      {/* Active Call Overlay */}
      <div className="fixed inset-0 z-50 bg-gradient-to-b from-[#1a2e1a] via-[#0d1f0d] to-[#000000] flex flex-col items-center justify-center" data-testid="active-call-overlay">
        {/* Call Status */}
        <div className="text-center mb-8">
          <div className="h-24 w-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/30">
            <Phone className="w-10 h-10 text-white" />
          </div>
          {/* During a warm transfer (we've added a third party and are
              consulting with them), show the TARGET's name/phone instead
              of the held external caller's. The operator is talking to
              the target right now — showing the caller's info would be
              confusing. After Complete Transfer the operator drops out;
              after Cancel the third_party state clears and we revert to
              the external caller. */}
          {(callData.third_party_active || callData.third_party_state === 'connected' || callData.third_party_state === 'dialing') && callData.third_party_target_name ? (
            <>
              <h2 className="text-2xl font-bold text-white mb-1" data-testid="active-call-third-party-name">
                {callData.third_party_target_name}
              </h2>
              <p className="text-white/60 text-sm mb-2">
                {formatPhone(callData.third_party_target_phone) || '\u00A0'}
              </p>
              <p className="text-white/40 text-xs mb-2">
                Warm transfer · {(callData.contact_name || formatPhone(callData.contact_phone || callData.to_phone))} on hold
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-1">
                {callData.contact_name || formatPhone(callData.contact_phone || callData.to_phone)}
              </h2>
              <p className="text-white/60 text-sm mb-2">
                {formatPhone(callData.contact_phone || callData.to_phone)}
              </p>
            </>
          )}
          <div className="flex items-center justify-center gap-2">
            {isOnHold ? (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">On Hold</Badge>
            ) : (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 animate-pulse">
                <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-1.5" />
                In Call
              </Badge>
            )}
            <span className="text-white/80 font-mono text-lg flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatDuration(callDuration)}
            </span>
          </div>
        </div>

        {/* 3-way action bar — visible only while we're dialing or merged
            with a third party. Replaces the standard call-controls grid
            so the user has unambiguous next-step actions. */}
        {threeWayState && (
          <div className="flex items-center justify-center gap-3 mb-6 w-full max-w-xs">
            {threeWayKind === 'transfer' ? (
              <>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleCompleteTransfer}
                  disabled={actionLoading === 'complete_transfer'}
                  data-testid="complete-transfer-btn"
                >
                  {actionLoading === 'complete_transfer' ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
                  Complete Transfer
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleEndMerger}
                  disabled={actionLoading === 'end_merger'}
                  data-testid="cancel-transfer-btn"
                >
                  {actionLoading === 'end_merger' ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
                  Cancel
                </Button>
              </>
            ) : threeWayState === 'merged' ? (
              <Button
                variant="destructive"
                className="w-full"
                onClick={handleEndMerger}
                disabled={actionLoading === 'end_merger'}
                data-testid="end-merger-btn"
              >
                {actionLoading === 'end_merger' ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
                End Merger
              </Button>
            ) : (
              <>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleMerge}
                  disabled={actionLoading === 'merge'}
                  data-testid="merge-btn"
                >
                  {actionLoading === 'merge' ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
                  Merge
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleEndMerger}
                  disabled={actionLoading === 'end_merger'}
                  data-testid="end-merger-btn"
                >
                  {actionLoading === 'end_merger' ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
                  End Merger
                </Button>
              </>
            )}
          </div>
        )}

        {/* Call Controls */}
        {!threeWayState && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 max-w-md w-full mb-6">
          {/* Speaker (mobile-only icon set — Android/desktop reroutes via
              setSinkId; iOS Safari falls back to a toast). Always
              visible per user spec — they want a speaker toggle on
              voice calls regardless of platform. iter145. */}
          <button
            className="flex flex-col items-center gap-1.5"
            onClick={async () => {
              const next = !isSpeakerOn;
              setIsSpeakerOn(next);
              // Twilio Voice JS SDK exposes an audio output device list;
              // calling setSinkId on the SDK's `_audioElement` works on
              // most Chromium browsers, including PWA on Android.
              const sdkAudio = liveSdkCall?._mediaHandler?.audio
                || liveSdkCall?._mediaHandler?._audioOutputElement
                || document.querySelector('audio[data-twilio-output], audio[data-twilio-incoming], audio');
              const res = await trySetSpeakerOn(sdkAudio, next);
              if (!res.ok) {
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints||0) > 1);
                if (isIOS) {
                  toast.info('iOS: Use the side speaker / Bluetooth toggle in the iOS call drawer to switch output.');
                } else {
                  toast.info('Speaker toggle not supported on this browser/device — system default in use.');
                }
              } else {
                toast.success(next ? 'Speakerphone ON' : 'Earpiece ON');
              }
            }}
            data-testid="speaker-btn"
          >
            <div className={`h-14 w-14 rounded-full flex items-center justify-center transition-colors ${
              isSpeakerOn ? 'bg-[#586D52]/30 border border-[#586D52]/60' : 'bg-white/10 border border-white/20'
            }`}>
              {isSpeakerOn ? <Volume2 className="w-6 h-6 text-[#a3c197]" /> : <VolumeX className="w-6 h-6 text-white" />}
            </div>
            <span className="text-white/70 text-xs">{isSpeakerOn ? 'Speaker' : 'Earpiece'}</span>
          </button>

          {/* Mute */}
          <button
            className="flex flex-col items-center gap-1.5"
            onClick={() => {
              const next = !isMuted;
              setIsMuted(next);
              try {
                if (liveSdkCall && typeof liveSdkCall.mute === 'function') {
                  liveSdkCall.mute(next);
                } else {
                  toast.error('Mute unavailable — call audio not ready yet');
                  setIsMuted(!next);
                }
              } catch (e) {
                console.error('mute failed:', e);
                setIsMuted(!next);
              }
            }}
            data-testid="mute-btn"
          >
            <div className={`h-14 w-14 rounded-full flex items-center justify-center transition-colors ${
              isMuted ? 'bg-red-500/30 border border-red-500/50' : 'bg-white/10 border border-white/20'
            }`}>
              {isMuted ? <MicOff className="w-6 h-6 text-red-400" /> : <Mic className="w-6 h-6 text-white" />}
            </div>
            <span className="text-white/70 text-xs">{isMuted ? 'Unmute' : 'Mute'}</span>
          </button>

          {/* Hold */}
          <button
            className="flex flex-col items-center gap-1.5"
            onClick={handleHold}
            disabled={actionLoading === 'hold'}
            data-testid="hold-btn"
          >
            <div className={`h-14 w-14 rounded-full flex items-center justify-center transition-colors ${
              isOnHold ? 'bg-yellow-500/30 border border-yellow-500/50' : 'bg-white/10 border border-white/20'
            }`}>
              {actionLoading === 'hold' ? (
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              ) : isOnHold ? (
                <Play className="w-6 h-6 text-yellow-400" />
              ) : (
                <Pause className="w-6 h-6 text-white" />
              )}
            </div>
            <span className="text-white/70 text-xs">{isOnHold ? 'Resume' : 'Hold'}</span>
          </button>

          {/* Transfer */}
          <button
            className="flex flex-col items-center gap-1.5"
            onClick={() => setShowTransferModal(true)}
            data-testid="transfer-btn"
          >
            <div className="h-14 w-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
              <ArrowRightLeft className="w-6 h-6 text-white" />
            </div>
            <span className="text-white/70 text-xs">Transfer</span>
          </button>

          {/* Add Caller */}
          <button
            className="flex flex-col items-center gap-1.5"
            onClick={() => {
              setTransferType('user');
              setTransferNumber('');
              setShowAddModal(true);
            }}
            data-testid="add-caller-btn"
          >
            <div className="h-14 w-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            <span className="text-white/70 text-xs">Add</span>
          </button>

          {/* Stop Recording (mid-call) — plays the "recording stopped"
              clip to both parties and halts Twilio conference recording.
              Cannot be re-enabled mid-call; that would violate the
              consent we played at the top of the call.
              iter122: Leads + Supervisors cannot disable recording —
              button is disabled for those roles. */}
          {(() => {
            const recordingLockedOn = ['lead', 'supervisor'].includes((user?.role || '').toLowerCase());
            return (
          <button
            className="flex flex-col items-center gap-1.5"
            onClick={handleStopRecording}
            disabled={stoppingRecording || !recordingActive || recordingLockedOn}
            data-testid="stop-recording-btn"
          >
            <div className={`h-14 w-14 rounded-full flex items-center justify-center transition-colors ${
              !recordingActive || recordingLockedOn
                ? 'bg-gray-500/20 border border-gray-500/30'
                : 'bg-white/10 border border-white/20'
            }`}>
              {stoppingRecording ? (
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              ) : recordingActive && !recordingLockedOn ? (
                <Circle className="w-6 h-6 text-red-400 fill-red-400" />
              ) : (
                <StopCircle className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <span className="text-white/70 text-xs">
              {recordingLockedOn ? 'Rec Locked' : recordingActive ? 'Stop Rec' : 'Rec Off'}
            </span>
          </button>
            );
          })()}
        </div>
        )}

        {/* End Call */}
        <Button
          className="h-16 w-16 rounded-full bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30"
          onClick={handleEndCall}
          disabled={actionLoading === 'end'}
          data-testid="end-call-btn"
        >
          {actionLoading === 'end' ? (
            <Loader2 className="w-7 h-7 animate-spin" />
          ) : (
            <PhoneOff className="w-7 h-7" />
          )}
        </Button>
      </div>

      {/* Transfer Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="z-[60]">
          <DialogHeader>
            <DialogTitle>Transfer Call</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Transfer Type Toggle */}
            <div className="flex rounded-lg bg-muted p-1">
              {[
                { key: 'user', label: 'In-App User' },
                { key: 'supervisor', label: 'Supervisor' },
                { key: 'external', label: 'External #' }
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTransferType(t.key)}
                  className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                    transferType === t.key
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`transfer-type-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Transfer to In-App User */}
            {transferType === 'user' && (
              <ScrollArea className="max-h-48">
                {eligibleUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No eligible users</p>
                ) : (
                  <div className="space-y-1">
                    {eligibleUsers.map(u => (
                      <button
                        key={u.id}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors"
                        onClick={() => handleTransfer(u.id, null, false)}
                        disabled={actionLoading === 'transfer'}
                        data-testid={`transfer-to-user-${u.id}`}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-xs">
                            {getFullName(u).split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium">{getFullName(u)}</p>
                          <p className="text-xs text-muted-foreground capitalize">{u.role}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            )}

            {/* Transfer to Supervisor */}
            {transferType === 'supervisor' && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground mb-3">
                  Transfer will route based on supervisor's settings (in-app or external forward)
                </p>
                <Button
                  className="bg-[#586D52] hover:bg-[#4a5d46]"
                  onClick={() => handleTransfer(null, null, true)}
                  disabled={actionLoading === 'transfer'}
                  data-testid="transfer-to-supervisor-btn"
                >
                  {actionLoading === 'transfer' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Users className="w-4 h-4 mr-2" />
                  )}
                  Transfer to Supervisor
                </Button>
              </div>
            )}

            {/* Transfer to External Number */}
            {transferType === 'external' && (
              <div className="space-y-2">
                <Input
                  placeholder="+1 (555) 123-4567"
                  value={transferNumber}
                  onChange={(e) => setTransferNumber(e.target.value)}
                  data-testid="transfer-external-input"
                />
                <Button
                  className="w-full bg-[#586D52] hover:bg-[#4a5d46]"
                  onClick={() => handleTransfer(null, transferNumber, false)}
                  disabled={actionLoading === 'transfer' || !transferNumber.trim()}
                  data-testid="transfer-external-btn"
                >
                  {actionLoading === 'transfer' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Phone className="w-4 h-4 mr-2" />
                  )}
                  Transfer to Number
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Participant Modal (3-way) */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="z-[60]">
          <DialogHeader>
            <DialogTitle>Add Caller (3-way)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex rounded-lg bg-muted p-1">
              {[
                { key: 'user', label: 'In-App User' },
                { key: 'external', label: 'External #' }
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTransferType(t.key)}
                  className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                    transferType === t.key
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`add-type-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {transferType === 'user' && (
              <ScrollArea className="max-h-48">
                {eligibleUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No eligible users</p>
                ) : (
                  <div className="space-y-1">
                    {eligibleUsers.map(u => (
                      <button
                        key={u.id}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors"
                        onClick={() => handleAddParticipant(u.id, null)}
                        disabled={actionLoading === 'add'}
                        data-testid={`add-user-${u.id}`}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-xs">
                            {getFullName(u).split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium">{getFullName(u)}</p>
                          <p className="text-xs text-muted-foreground capitalize">{u.role}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            )}

            {transferType === 'external' && (
              <div className="space-y-2">
                <Input
                  placeholder="+1 (555) 123-4567"
                  value={transferNumber}
                  onChange={(e) => setTransferNumber(e.target.value)}
                  data-testid="add-external-input"
                />
                <Button
                  className="w-full bg-[#586D52] hover:bg-[#4a5d46]"
                  onClick={() => handleAddParticipant(null, transferNumber)}
                  disabled={actionLoading === 'add' || !transferNumber.trim()}
                  data-testid="add-external-btn"
                >
                  {actionLoading === 'add' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2" />
                  )}
                  Add to Call
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActiveExternalCall;
